import { useState, useEffect, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Radio } from "lucide-react"
import { useLocation, useNavigate } from "react-router"

import { OnboardingEmptyState, LoadingPage } from "@/components/System"
import { useToast } from "@/hooks"
import { FetchStatus } from "@/hooks/useFetch"
import { Permission, Workspace } from "@/types"
import {
  transformOrganizationLimitUsage,
  useGetClientChannelsApi,
  useGetOrganizationLimitUsageApi,
  clientChannelsResponseToClientChannels,
} from "@/hooks/api"
import { ClientChannelListItem, ClientChannel } from "@/types"

import { ChannelList, ChannelUsageCard } from "./ChannelList"
import { ChannelDetail } from "./ChannelDetail"
import { ChannelDialog } from "./ChannelDialog"

export interface ChannelsTabProps {
  workspace: Workspace
}

const toListItemFromChannel = (
  channel: ClientChannel
): ClientChannelListItem => ({
  id: channel.id,
  name: channel.name,
  slug: channel.slug,
  type: channel.type,
  agentId: "",
  isHealthy: channel.isHealthy,
  isPublished: channel.isPublished,
  lastPublishedSlug: channel.lastPublishedSlug,
  lastPublishedVersion: channel.lastPublishedVersion,
  publishedAt: channel.publishedAt,
  createdAt: channel.createdAt,
  updatedAt: channel.updatedAt,
})

export const ChannelsTab = ({ workspace }: ChannelsTabProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [channels, setChannels] = useState<ClientChannelListItem[]>([])
  const [selectedChannelItem, setSelectedChannelItem] = useState<
    ClientChannelListItem | undefined
  >()
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [prefillAgentId, setPrefillAgentId] = useState("")
  const { alertToast } = useToast()
  const organizationId = workspace.organizationId || ""

  // APIs
  const {
    data: channelsRespData,
    error: channelsRespError,
    status: channelsStatus,
    fetchFn: getChannels,
  } = useGetClientChannelsApi(organizationId, workspace.id)
  const { data: limitUsageResp, fetchFn: getLimitUsage } =
    useGetOrganizationLimitUsageApi(organizationId)

  const refreshLimitUsage = useCallback(() => {
    if (!organizationId) return
    getLimitUsage({ preserveData: true }).catch(() => {
      alertToast({
        title: t("workspace.channels.usage.errors.loadFailedTitle"),
        description: t("workspace.channels.usage.errors.loadFailedDescription"),
      })
    })
  }, [alertToast, getLimitUsage, organizationId, t])

  useEffect(() => {
    setChannels([])
    setSelectedChannelItem(undefined)
    setShowCreateDialog(false)
    setPrefillAgentId("")
  }, [workspace.id])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (
      searchParams.get("tab") !== "channels" ||
      searchParams.get("intent") !== "create-channel"
    ) {
      return
    }

    setPrefillAgentId(searchParams.get("agentId") || "")
    setShowCreateDialog(true)
    searchParams.delete("intent")
    searchParams.delete("agentId")
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    })
  }, [location.pathname, location.search, navigate])

  // Load channels on mount
  useEffect(() => {
    if (workspace.id) {
      getChannels({
        searchParams: new URLSearchParams({
          limit: "100",
          offset: "0",
        }),
      })
      refreshLimitUsage()
    }
  }, [workspace.id, getChannels, refreshLimitUsage])

  // Handle channels data
  useEffect(() => {
    if (!channelsRespData || !channelsRespData.data) return
    const channelsData = clientChannelsResponseToClientChannels(
      channelsRespData.data
    )
    setChannels(channelsData)
    setSelectedChannelItem((current) => {
      if (!current) return channelsData[0]
      return (
        channelsData.find((channel) => channel.id === current.id) ||
        channelsData[0]
      )
    })
  }, [channelsRespData])

  // Handle channels error
  useEffect(() => {
    if (!channelsRespError) return
    alertToast({
      title: t("workspace.channels.messages.error.loadChannels.title"),
      description: t(
        "workspace.channels.messages.error.loadChannels.description"
      ),
    })
  }, [channelsRespError, alertToast, t])

  const handleChannelSelect = useCallback((channel: ClientChannelListItem) => {
    setSelectedChannelItem(channel)
  }, [])

  const channelUsage = useMemo(() => {
    if (!limitUsageResp?.data) return null
    return (
      transformOrganizationLimitUsage(limitUsageResp.data).items.find(
        (item) => item.key === "channels"
      ) ?? null
    )
  }, [limitUsageResp?.data])

  const handleCreateChannel = useCallback(() => {
    setPrefillAgentId("")
    setShowCreateDialog(true)
  }, [])

  const handleChannelCreated = useCallback(
    (newChannel: ClientChannel) => {
      setSelectedChannelItem(toListItemFromChannel(newChannel))
      setShowCreateDialog(false)
      // Refetch channels to ensure consistency
      if (workspace.id) {
        getChannels({
          searchParams: new URLSearchParams({
            limit: "100",
            offset: "0",
          }),
        })
        refreshLimitUsage()
      }
    },
    [workspace.id, getChannels, refreshLimitUsage]
  )

  const handleChannelUpdated = useCallback(
    (updatedChannel: ClientChannel) => {
      setSelectedChannelItem(toListItemFromChannel(updatedChannel))
      // Refetch channels to ensure consistency
      if (workspace.id) {
        getChannels({
          searchParams: new URLSearchParams({
            limit: "100",
            offset: "0",
          }),
        })
        refreshLimitUsage()
      }
    },
    [workspace.id, getChannels, refreshLimitUsage]
  )

  const handleChannelDeleted = useCallback(
    (channelId: string) => {
      if (selectedChannelItem?.id === channelId) {
        const remainingChannels = channels.filter((ch) => ch.id !== channelId)
        if (remainingChannels.length > 0) {
          setSelectedChannelItem(remainingChannels[0])
        } else {
          setSelectedChannelItem(undefined)
        }
      }
      // Refetch channels to ensure consistency
      if (workspace.id) {
        getChannels({
          searchParams: new URLSearchParams({
            limit: "100",
            offset: "0",
          }),
        })
        refreshLimitUsage()
      }
    },
    [
      selectedChannelItem,
      channels,
      workspace.id,
      getChannels,
      refreshLimitUsage,
    ]
  )

  // Show full-width onboarding when no channels exist
  if (channels.length === 0) {
    if (channelsStatus === FetchStatus.Loading) {
      return <LoadingPage topOffset="120px" />
    }

    return (
      <div className="space-y-4">
        <ChannelUsageCard usage={channelUsage} />
        <OnboardingEmptyState
          icon={Radio}
          title={t("workspace.channels.onboarding.title")}
          description={t("workspace.channels.onboarding.description")}
          steps={[
            { title: t("workspace.channels.onboarding.steps.create") },
            { title: t("workspace.channels.onboarding.steps.configure") },
            { title: t("workspace.channels.onboarding.steps.publish") },
          ]}
          primaryAction={{
            label: t("workspace.channels.list.newChannel"),
            onClick: handleCreateChannel,
            permission: {
              required: Permission.WsUpdateResources,
              workspace,
              disabledTooltip: t("common.noPermission"),
            },
          }}
        />

        {/* Create Channel Dialog */}
        {showCreateDialog && (
          <ChannelDialog
            open={showCreateDialog}
            workspace={workspace}
            onOpenChange={setShowCreateDialog}
            onCreated={handleChannelCreated}
            prefillAgentId={prefillAgentId}
          />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:grid lg:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-200px)]">
        {/* Left Panel - Channel List */}
        <ChannelList
          channels={channels}
          selectedChannelItem={selectedChannelItem}
          channelUsage={channelUsage}
          onChannelSelect={handleChannelSelect}
          onCreateChannel={handleCreateChannel}
          workspace={workspace}
        />

        {/* Right Panel - Channel Detail */}
        {selectedChannelItem && (
          <ChannelDetail
            selectedChannelItem={selectedChannelItem}
            workspace={workspace}
            onChannelUpdated={handleChannelUpdated}
            onChannelDeleted={handleChannelDeleted}
          />
        )}
      </div>

      {/* Create Channel Dialog */}
      {showCreateDialog && (
        <ChannelDialog
          open={showCreateDialog}
          workspace={workspace}
          onOpenChange={setShowCreateDialog}
          onCreated={handleChannelCreated}
          prefillAgentId={prefillAgentId}
        />
      )}
    </div>
  )
}

export default ChannelsTab
